import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ManagerLayout from '../components/ManagerLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Upload, AlertTriangle, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagerDataManagement = ({ manager, onLogout }) => {
  const [importData, setImportData] = useState('');
  const [clearExisting, setClearExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  const [bulkUploadMode, setBulkUploadMode] = useState('non_destructive');

  useEffect(() => {
    // Check if user is Administrator
    if (manager?.role !== 'Administrator') {
      toast.error('Access denied. Administrator role required.');
      onLogout();
    }
  }, [manager, onLogout]);

  const handleExport = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('managerToken');
      const response = await axios.get(`${API}/admin/export-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create JSON file and download
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `punchclock_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const token = sessionStorage.getItem('managerToken');
      const response = await axios.post(
        `${API}/employees/bulk-update?mode=${bulkUploadMode}`, 
        formData, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setBulkUploadResults(response.data);
      
      const totalChanges = response.data.success.length + response.data.created.length;
      if (totalChanges > 0) {
        toast.success(`Bulk update complete! ${response.data.success.length} updated, ${response.data.created.length} created, ${response.data.errors.length} errors`);
      } else if (response.data.errors.length > 0) {
        toast.error(`No changes made. ${response.data.errors.length} errors found.`);
      } else {
        toast.info(`No changes needed. ${response.data.skipped.length} rows skipped.`);
      }
    } catch (error) {
      console.error('Bulk CSV upload error:', error);
      toast.error(error.response?.data?.detail || 'Error uploading CSV');
    } finally {
      setLoading(false);
      event.target.value = ''; // Reset file input
    }
  };

  const downloadBulkTemplate = () => {
    const csv = `email,full_name,phone_last4,role,status,unpaid_breaks
john.doe@company.com,John Doe,1234,Associate,active,Yes
jane.smith@company.com,Jane Smith,5678,Manager,active,No`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_bulk_update_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      toast.error('Please paste JSON data to import');
      return;
    }

    console.log('handleImport called, clearExisting:', clearExisting);

    // If clearExisting is true, show confirmation dialog instead of native confirm
    if (clearExisting) {
      console.log('Clear existing is true, showing confirmation dialog');
      setShowConfirmDialog(true);
      return;
    }

    // Proceed with import (clearExisting is false)
    console.log('Proceeding with import, clearExisting:', clearExisting);
    await performImport();
  };

  const performImport = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('managerToken');
      const parsedData = JSON.parse(importData);

      console.log('Sending import request with clear_existing:', clearExisting);

      const response = await axios.post(
        `${API}/admin/import-data?clear_existing=${clearExisting}`,
        parsedData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Import response:', response.data);
      toast.success(`Import successful! ${JSON.stringify(response.data.imported_counts)}`);
      
      // Small delay before clearing to prevent false error message
      setTimeout(() => {
        setImportData('');
        setClearExisting(false);
      }, 100);
    } catch (error) {
      console.error('Import error:', error);
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format. Please check your data.');
      } else {
        toast.error(error.response?.data?.detail || 'Import failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = () => {
    console.log('User confirmed destructive import');
    setShowConfirmDialog(false);
    performImport();
  };

  const handleCancelImport = () => {
    console.log('User cancelled destructive import');
    setShowConfirmDialog(false);
  };

  return (
    <ManagerLayout manager={manager} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">
          Data Management
        </h1>

        {/* Bulk Employee Update Section */}
        <Card className="p-6 mb-6 border-2 border-purple-200 bg-purple-50">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Users className="w-8 h-8 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Bulk Employee Update
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Upload a CSV file to update or create employees in bulk. 
                The <code className="bg-white px-1 rounded">email</code> field is used as the unique identifier to match existing employees.
              </p>
              
              {/* CSV Columns Info */}
              <div className="bg-white rounded-lg p-3 mb-4 border border-purple-200">
                <p className="text-xs font-medium text-gray-700 mb-2">CSV Columns:</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">email *</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">full_name</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">phone_last4</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">role</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">status</span>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded">unpaid_breaks</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">* Email is required and used to match employees. unpaid_breaks accepts Yes/No.</p>
              </div>
              
              {/* Mode Selection */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Update Mode:</p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="updateMode"
                      value="non_destructive"
                      checked={bulkUploadMode === 'non_destructive'}
                      onChange={(e) => setBulkUploadMode(e.target.value)}
                      className="text-purple-600"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-800">Non-Destructive</span>
                      <p className="text-xs text-gray-500">Only updates fields with values in CSV. Empty fields are skipped.</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="updateMode"
                      value="overwrite"
                      checked={bulkUploadMode === 'overwrite'}
                      onChange={(e) => setBulkUploadMode(e.target.value)}
                      className="text-purple-600"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-800">Full Overwrite</span>
                      <p className="text-xs text-gray-500">Replaces all fields. Empty fields reset to defaults.</p>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3 mb-4">
                <Button
                  onClick={downloadBulkTemplate}
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  data-testid="download-bulk-template"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
                
                <label htmlFor="bulk-csv-upload" className="cursor-pointer">
                  <Button
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={loading}
                    onClick={() => document.getElementById('bulk-csv-upload').click()}
                    data-testid="upload-bulk-csv"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {loading ? 'Uploading...' : 'Upload CSV'}
                  </Button>
                  <input
                    id="bulk-csv-upload"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleBulkCSVUpload}
                  />
                </label>
              </div>

              {/* Upload Results */}
              {bulkUploadResults && (
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-800">Upload Results</h3>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      Mode: {bulkUploadResults.mode === 'non_destructive' ? 'Non-Destructive' : 'Full Overwrite'}
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-2 text-center mb-4">
                    <div className="p-2 bg-blue-50 rounded">
                      <p className="text-lg font-bold text-blue-600">{bulkUploadResults.total}</p>
                      <p className="text-xs text-gray-600">Total</p>
                    </div>
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-lg font-bold text-green-600">{bulkUploadResults.success.length}</p>
                      <p className="text-xs text-gray-600">Updated</p>
                    </div>
                    <div className="p-2 bg-teal-50 rounded">
                      <p className="text-lg font-bold text-teal-600">{bulkUploadResults.created.length}</p>
                      <p className="text-xs text-gray-600">Created</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-lg font-bold text-gray-600">{bulkUploadResults.skipped.length}</p>
                      <p className="text-xs text-gray-600">Skipped</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded">
                      <p className="text-lg font-bold text-red-600">{bulkUploadResults.errors.length}</p>
                      <p className="text-xs text-gray-600">Errors</p>
                    </div>
                  </div>

                  {bulkUploadResults.success.length > 0 && (
                    <div className="mb-3">
                      <h4 className="font-medium text-green-800 text-sm mb-1">Successfully Updated:</h4>
                      <div className="max-h-24 overflow-y-auto text-xs space-y-1">
                        {bulkUploadResults.success.map((item, idx) => (
                          <div key={idx} className="text-gray-700">
                            ✓ {item.email} - updated: {item.fields_updated.join(', ')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {bulkUploadResults.created.length > 0 && (
                    <div className="mb-3">
                      <h4 className="font-medium text-teal-800 text-sm mb-1">Newly Created:</h4>
                      <div className="max-h-24 overflow-y-auto text-xs space-y-1">
                        {bulkUploadResults.created.map((item, idx) => (
                          <div key={idx} className="text-gray-700">
                            + {item.name} ({item.email})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {bulkUploadResults.errors.length > 0 && (
                    <div>
                      <h4 className="font-medium text-red-800 text-sm mb-1">Errors:</h4>
                      <div className="max-h-24 overflow-y-auto text-xs space-y-1">
                        {bulkUploadResults.errors.map((item, idx) => (
                          <div key={idx} className="text-gray-700">
                            ✗ Row {item.row}: {item.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => setBulkUploadResults(null)}
                    variant="outline"
                    size="sm"
                    className="mt-3"
                  >
                    Clear Results
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Export Section */}
        <Card className="p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Download className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Export All Data
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Download all database collections as a JSON file. This includes employees, locations, 
                time entries, exceptions, and edit logs. Use this to backup your data or migrate to another environment.
              </p>
              <Button
                onClick={handleExport}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All Data
              </Button>
            </div>
          </div>
        </Card>

        {/* Import Section */}
        <Card className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Upload className="w-8 h-8 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Import Data
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Import data from a previously exported JSON file. Paste the JSON content below and click Import.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                <div className="flex gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">⚠️ Important Notes:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Import will ADD data to existing records (no overwrite by default)</li>
                      <li>Check "Clear Existing Data" to replace all data</li>
                      <li>Your current admin account will be preserved (not deleted or replaced)</li>
                      <li>Make sure you have a backup before clearing data</li>
                      <li>Duplicate IDs may cause import errors</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste JSON Data:
                </label>
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  placeholder='{"collections": {"employees": [...], "time_entries": [...], ...}}'
                  className="w-full h-64 border border-gray-300 rounded-md p-3 font-mono text-sm"
                  disabled={loading}
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={clearExisting}
                    onChange={(e) => setClearExisting(e.target.checked)}
                    className="w-4 h-4"
                    disabled={loading}
                  />
                  <span className="text-sm font-medium text-red-600">
                    Clear all existing data before import (⚠️ Your admin account will be preserved)
                  </span>
                </label>
              </div>

              <Button
                onClick={() => {
                  console.log('Import button clicked!');
                  console.log('importData length:', importData.length);
                  console.log('loading:', loading);
                  console.log('clearExisting:', clearExisting);
                  handleImport();
                }}
                disabled={loading || !importData.trim()}
                className="bg-green-600 hover:bg-green-700"
                data-testid="import-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {loading ? 'Importing...' : 'Import Data'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Instructions */}
        <Card className="p-6 mt-6 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            How to Migrate Data from Production to Preview:
          </h3>
          <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
            <li>Open your <strong>production app</strong> (deployed version)</li>
            <li>Go to Data Management page and click <strong>"Export All Data"</strong></li>
            <li>Save the downloaded JSON file</li>
            <li>Open the JSON file in a text editor and copy all content</li>
            <li>Come back to this <strong>preview app</strong> page</li>
            <li>Paste the JSON content in the text area above</li>
            <li>Check "Clear existing data" if you want to replace all preview data</li>
            <li>Click <strong>"Import Data"</strong></li>
          </ol>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  ⚠️ Destructive Action Warning
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  This will <strong className="text-red-600">DELETE ALL EXISTING DATA</strong> before importing.
                </p>
                <p className="text-sm text-gray-700 mb-2">
                  The following will be deleted:
                </p>
                <ul className="text-sm text-gray-700 list-disc list-inside mb-3">
                  <li>All employees (except your admin account)</li>
                  <li>All locations</li>
                  <li>All time entries</li>
                  <li>All exceptions</li>
                  <li>All edit logs</li>
                </ul>
                <p className="text-sm font-bold text-red-600">
                  This action cannot be undone!
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={handleCancelImport}
                variant="outline"
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmImport}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete & Import
              </Button>
            </div>
          </div>
        </div>
      )}
    </ManagerLayout>
  );
};

export default ManagerDataManagement;
